/**
 * Registration form with optional profile picture upload
 */
import { useRef, useState } from "react";
import { Button } from "./ui/button";
import { Input } from "./ui/input";
import { Label } from "./ui/label";
import {
  Card,
  CardContent,
  CardDescription,
  CardHeader,
  CardTitle,
} from "./ui/card";
import { Alert, AlertDescription } from "./ui/alert";
import { Book, ArrowLeft, Check, X, AlertTriangle, Loader2 } from "lucide-react";
import { useApp } from "@/contexts/AppContext";
import { useAuth } from "@/contexts/AuthContext";
import ProfilePictureUploader, { type ProfilePictureUploaderHandle } from "@/components/account/settings/ProfilePictureUploader";
import { toast } from "sonner";
import { AuthInfoScreen } from "@/components/auth/AuthInfoScreen";
import {
  arePasswordRequirementsMet,
  getPasswordRequirements,
  passwordRequirementItems,
} from "@/utils/passwordValidation";

interface RegisterProps {
  navigate: (page: string) => void;
  onNavigateToSearchPage?: (target: "categories" | "search") => void;
}

export function Register({ navigate }: RegisterProps) {
  const { t } = useApp();
  const { register } = useAuth();
  const maxFieldLength = 255;
  const [formData, setFormData] = useState({
    username: "",
    email: "",
    password: "",
    confirmPassword: "",
  });
  const [registrationComplete, setRegistrationComplete] = useState(false);
  const [verificationRequired, setVerificationRequired] = useState<boolean | null>(null);
  const [isLoading, setIsLoading] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const uploaderRef = useRef<ProfilePictureUploaderHandle | null>(null);
  const pageStyle = {
    background:
      "linear-gradient(135deg, color-mix(in srgb, var(--primary) 10%, var(--background)), color-mix(in srgb, var(--surface) 80%, var(--background)))",
  } as const;
  const requirementsStyle = {
    backgroundColor:
      "color-mix(in srgb, var(--foreground) 6%, var(--surface))",
  } as const;
  const titleStyle = {
    color: "var(--primary)",
  } as const;
  const descriptionStyle = {
    color: "color-mix(in srgb, var(--foreground) 82%, var(--background))",
  } as const;
  const requirementMetStyle = {
    color: "color-mix(in srgb, var(--primary) 82%, var(--foreground))",
  } as const;
  const requirementUnmetStyle = {
    color: "color-mix(in srgb, var(--foreground) 62%, var(--background))",
  } as const;
  const matchTextStyle = {
    color: "color-mix(in srgb, var(--primary) 82%, var(--foreground))",
  } as const;
  const mismatchTextStyle = {
    color: "var(--destructive)",
  } as const;
  const linkStyle = {
    color: "color-mix(in srgb, var(--primary) 82%, var(--foreground))",
  } as const;
  const borderPanelStyle = {
    borderColor: "var(--border)",
  } as const;

  const passwordRequirements = getPasswordRequirements(formData.password);
  const isPasswordValid = arePasswordRequirementsMet(passwordRequirements);
  const passwordsMatch =
    formData.password === formData.confirmPassword &&
    formData.confirmPassword !== "";

  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault();
    if (!isPasswordValid || !passwordsMatch) return;
    setIsLoading(true);
    setError(null);

    try {
      const response = await register({
        username: formData.username,
        email: formData.email,
        password: formData.password
      });

      setVerificationRequired(response.verificationRequired);
      if (response.verificationRequired) {
        toast.success(t("auth.verificationEmailSent"));
        toast.info(t("auth.verifyBeforeLogin"));
      } else {
        toast.info(t("auth.verificationNotRequired"));
        toast.info(t("auth.verificationNotRequiredHint"));
      }

      if (uploaderRef.current?.hasPendingImage()) {
        const uploaded = await uploaderRef.current.uploadPendingImage({
          registrationToken: response.profilePictureUploadToken,
        });
        if (!uploaded && !response.profilePictureUploadToken) {
          toast.warning(t("profilePicture.uploadWarning"));
        }
      }

      setRegistrationComplete(true);

    } catch (err: any) {
      console.error("Registrierung fehlgeschlagen:", err);
      const msg = err.response?.data?.message || err.message || "common.genericError";
      setError(t(msg)); //TODO: FIX THIS ERROR HANDLING
    } finally {
      setIsLoading(false);
    }
  };

  if (registrationComplete) {
    const emailVerificationRequired = verificationRequired !== false;
    const title = emailVerificationRequired
      ? t("auth.verifyEmailTitle")
      : t("auth.registrationCompleteTitle");
    const subtitle = emailVerificationRequired
      ? t("auth.verifyEmailSubtitle")
      : t("auth.registrationCompleteSubtitle");
    const body = emailVerificationRequired
      ? t("auth.verifyEmailSent").replace("{{email}}", formData.email)
      : t("auth.registrationCompleteBody");
    const nextStep = emailVerificationRequired
      ? t("auth.verifyEmailNextStep")
      : t("auth.registrationCompleteNextStep");

    return (
      <AuthInfoScreen
        title={title}
        subtitle={subtitle}
        body={body}
        nextStep={nextStep}
        primaryActionLabel={t("auth.login")}
        onPrimaryAction={() => navigate("login")}
        secondaryActionLabel={t("common.back")}
        onSecondaryAction={() => navigate("home")}
      />
    );
  }

  return (
    <div
      className="flex items-center justify-center px-4 py-6"
      style={{
        ...pageStyle,
        minHeight: "100vh",
        maxHeight: "calc(100vh / var(--ui-scale))",
        overflowY: "auto",
      }}
    >
      <div className="w-full max-w-md">
        {/* Logo */}
        <div className="text-center mb-8">
          <div className="inline-flex items-center gap-3 mb-4">
            <div className="icon-box">
              <Book className="w-8 h-8 icon-primary" />
            </div>
            <div className="text-left">
              <h1 className="text-primary-heading">{t("header.title")}</h1>
              <p className="text-xs text-secondary">{t("header.subtitle")}</p>
            </div>
          </div>
        </div>

        <Card>
          <CardHeader>
            <CardTitle className="card-title-text" style={titleStyle}>
              {t("auth.register")}
            </CardTitle>
            <CardDescription className="card-description-text" style={descriptionStyle}>
              {t("auth.registerSubtitle")}
            </CardDescription>
          </CardHeader>
          <CardContent>
            {error && (
              <Alert variant="destructive" className="mb-4">
                <AlertTriangle className="h-4 w-4" />
                <AlertDescription className="text-sm">
                  {error}
                </AlertDescription>
              </Alert>
            )}
            <form onSubmit={handleSubmit} className="space-y-4">
              <div className="space-y-2">
                <Label htmlFor="username">{t("account.username")}</Label>
                <Input
                  disabled={isLoading}
                  id="username"
                  type="text"
                  maxLength={maxFieldLength}
                  placeholder={t("auth.usernamePlaceholder")}
                  value={formData.username}
                  onChange={(e) =>
                    setFormData({
                      ...formData,
                      username: e.target.value,
                    })
                  }
                  required
                />
              </div>

              <div className="space-y-2">
                <Label htmlFor="email">{t("auth.email")}</Label>
                <Input
                  disabled={isLoading}
                  id="email"
                  type="email"
                  maxLength={maxFieldLength}
                  placeholder={t("auth.emailPlaceholder")}
                  value={formData.email}
                  onChange={(e) =>
                    setFormData({
                      ...formData,
                      email: e.target.value,
                    })
                  }
                  required
                />
              </div>

              <div className="space-y-2">
                <Label htmlFor="password">{t("auth.password")}</Label>
                <Input
                  disabled={isLoading}
                  id="password"
                  type="password"
                  maxLength={maxFieldLength}
                  placeholder="••••••••"
                  value={formData.password}
                  onChange={(e) =>
                    setFormData({
                      ...formData,
                      password: e.target.value,
                    })
                  }
                  required
                />
              </div>

              {/* Password Requirements */}
              {formData.password && (
                <div className="p-3 rounded-lg space-y-2" style={requirementsStyle}>
                  <p className="text-xs text-secondary mb-2">
                    {t("auth.passwordRequirementsTitle")}
                  </p>
                  <div className="space-y-1 text-sm">
                    {passwordRequirementItems.map(({ key, labelKey }) => {
                      const isMet = passwordRequirements[key];
                      return (
                        <div
                          key={key}
                          className="flex items-center gap-2"
                          style={isMet ? requirementMetStyle : requirementUnmetStyle}
                        >
                          {isMet ? (
                            <Check className="w-4 h-4" />
                          ) : (
                            <X className="w-4 h-4" />
                          )}
                          <span>{t(labelKey)}</span>
                        </div>
                      );
                    })}
                  </div>
                </div>
              )}

              <div className="space-y-2">
                <Label htmlFor="confirm-password">
                  {t("auth.confirmPassword")}
                </Label>
                <Input
                  disabled={isLoading}
                  id="confirm-password"
                  type="password"
                  maxLength={maxFieldLength}
                  placeholder="••••••••"
                  value={formData.confirmPassword}
                  onChange={(e) =>
                    setFormData({
                      ...formData,
                      confirmPassword: e.target.value,
                    })
                  }
                  required
                />
                {formData.confirmPassword && !passwordsMatch && (
                  <p className="text-sm flex items-center gap-1" style={mismatchTextStyle}>
                    <X className="w-4 h-4" />
                    {t("auth.passwordsDoNotMatch")}
                  </p>
                )}
                {formData.confirmPassword && passwordsMatch && (
                  <p className="text-sm flex items-center gap-1" style={matchTextStyle}>
                    <Check className="w-4 h-4" />
                    {t("auth.passwordsMatch")}
                  </p>
                )}
              </div>

              <div className="space-y-2">
                <Label>{t("profilePicture.title")}</Label>
                <div className="rounded-lg border p-3" style={borderPanelStyle}>
                  <ProfilePictureUploader
                    ref={uploaderRef}
                    enableUpload
                    showUploadButton={false}
                  />
                </div>
                <p className="text-xs text-secondary">
                  {t("profilePicture.registrationNote")}
                </p>
              </div>

              <Button
                type="submit"
                className="w-full"
                disabled={!isPasswordValid || !passwordsMatch || isLoading}
              >
                {isLoading ? (
                  <>
                    <Loader2 className="w-4 h-4 mr-2 animate-spin" />
                    {t("auth.registrationLoading")}
                  </>
                ) : (
                  t("auth.registerButton")
                )}
              </Button>

              <div className="text-center text-sm text-secondary">
                {t("auth.hasAccount")}{" "}
                <button
                  type="button"
                  onClick={() => navigate("login")}
                  className="underline-offset-4 hover:underline"
                  style={linkStyle}
                  disabled={isLoading}
                >
                  {t("auth.loginButton")}
                </button>
              </div>
            </form>
          </CardContent>
        </Card>

        <Button
          variant="ghost"
          onClick={() => navigate("home")}
          className="w-full mt-4 gap-2 text-secondary"
        >
          <ArrowLeft className="w-4 h-4" />
          {t("common.back")}
        </Button>
      </div>
    </div>
  );
}
